import React, { useEffect, useState } from 'react';
import './styles.scss';

interface HudState {
  visible: boolean;
  title?: string;
  subtitle?: string;
  speedLevel?: number;
  speedLevels?: number;
  speedLabel?: string;
  tilt?: boolean;
  scale?: number;
  noScale?: boolean;
}

const Keys: React.FC<{ keys: string[] }> = ({ keys }) => (
  <span className="placement-hud__keys">
    {keys.map((k) => (
      <kbd key={k} className="placement-hud__key">{k}</kbd>
    ))}
  </span>
);

const Row: React.FC<{ keys: string[]; label: string }> = ({ keys, label }) => (
  <li className="placement-hud__row">
    <Keys keys={keys} />
    <span className="placement-hud__label">{label}</span>
  </li>
);

// The key legend shown while an object is being placed (admin tool and house
// furniture alike). Sent by objectPlacementService.ts; it never takes focus, the
// player keeps walking around while placing.
const PlacementHud: React.FC = () => {
  const [hud, setHud] = useState<HudState>({ visible: false });

  useEffect(() => {
    const onHud = (e: Event) => setHud((e as CustomEvent).detail as HudState);
    window.addEventListener('skymp5-client:placementHud', onHud);
    return () => window.removeEventListener('skymp5-client:placementHud', onHud);
  }, []);

  if (!hud.visible) return null;

  const level = hud.speedLevel ?? 0;
  const levels = hud.speedLevels ?? 5;

  return (
    <div className="placement-hud" role="status">
      <div className="placement-hud__panel">
        <div className="placement-hud__head">
          <div className="placement-hud__who">
            <span className="placement-hud__title">{hud.title}</span>
            {hud.subtitle ? <span className="placement-hud__subtitle">{hud.subtitle}</span> : null}
          </div>
          <div className="placement-hud__speed" title="Vitesse de déplacement">
            <span className="placement-hud__speed-label">Vitesse</span>
            <span className="placement-hud__steps" aria-hidden="true">
              {Array.from({ length: levels }, (_, i) => (
                <span key={i} className={i <= level ? 'is-on' : ''} style={{ height: `${6 + i * 3}px` }} />
              ))}
            </span>
            <span className="placement-hud__speed-value">{hud.speedLabel}</span>
          </div>
        </div>

        <ul className="placement-hud__list">
          <Row keys={['↑', '↓', '←', '→']} label="Déplacer" />
          <Row keys={['Pg haut', 'Pg bas']} label="Monter, descendre" />
          <Row keys={['Début', 'Fin']} label="Tourner" />
          {hud.tilt ? <Row keys={['Pavé 8', 'Pavé 2']} label="Pencher avant, arrière" /> : null}
          {hud.tilt ? <Row keys={['Pavé 4', 'Pavé 6']} label="Pencher gauche, droite" /> : null}
          {hud.tilt ? <Row keys={['Pavé 5']} label="Remettre droit" /> : null}
          {hud.tilt && !hud.noScale ? <Row keys={['Pavé 7', 'Pavé 9']} label={`Taille : x${hud.scale ?? 1}`} /> : null}
          {hud.tilt && !hud.noScale ? <Row keys={['Pavé 1']} label="Taille normale" /> : null}
          {hud.tilt ? <Row keys={['Souris']} label="Glisser le repère jaune : déplacer" /> : null}
          {hud.tilt ? <Row keys={['Maj', 'Souris']} label="Monter, descendre" /> : null}
          {hud.tilt ? <Row keys={['Molette']} label="Tourner (souris)" /> : null}
          {hud.tilt && !hud.noScale ? <Row keys={['Ctrl', 'Molette']} label="Taille (souris)" /> : null}
          <Row keys={['-', '+']} label="Vitesse" />
          <Row keys={['Ctrl droit']} label="Maintenir : précision" />
          <Row keys={['Entrée']} label="Valider" />
          <Row keys={['Échap']} label="Annuler" />
        </ul>

        <p className="placement-hud__foot">Tu peux marcher normalement pendant le placement.</p>
      </div>
    </div>
  );
};

export default PlacementHud;
